import { z } from 'zod';
import { v1UserRefSchema } from './actor';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { V1_SHADOW_AI_CATEGORY_IDS } from './vocabulary';
/**
 * Public Shadow AI contract for `/api/v1` (KIR-4431, slice 2).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`.
 *
 * Five read resources, all under `shadow-ai/`:
 *
 * | Resource              | What a row is                                       |
 * | --------------------- | --------------------------------------------------- |
 * | `browser-tools`       | An AI web app your developers visited, by domain    |
 * | `installed-tools`     | An AI tool installed on their machines              |
 * | `running-processes`   | One AI process running right now                    |
 * | `summary`             | Org-wide counters — a single object, not a list      |
 * | `browser-agent-sites` | A site an AI agent reached, per agent type (KIR-5144) |
 *
 * **The module is behind the `shadow_ai` feature flag**; while it is off every
 * endpoint here answers `409 Conflict`, per the decision recorded on KIR-4431.
 * The internal routes answer 403 or 404 for the same condition and keep doing
 * so — a route-level adaptation for a different audience. The one exception is
 * `browser-agent-sites`, gated on `browser_agent_activity` alone because that is
 * the only flag the app's screen sits behind.
 *
 * **Results are scoped to the caller's own rows unless the key carries
 * manager, owner or support.** These resources sit on an any-role gate
 * internally, and the internal routes narrow a non-privileged session to its
 * own findings. The gate alone would let a member-minted key read the whole
 * organization, so `/api/v1` replicates the narrowing through the same
 * predicate the app uses.
 *
 * **Writes are deliberately absent.** Domain classification, bulk-classify and
 * ignore-domain are writes on the internal surface; the parent scope makes
 * Shadow AI read-only in this phase. They are not overlooked.
 */
/** Where a tool sits on the AI-risk scale. Critical is not used here. */
export const v1ShadowAiRiskSchema = z
    .enum(['low', 'medium', 'high'])
    .describe('How risky Kirin considers this tool.');
/** Which way a tool's usage is moving. */
export const v1ShadowAiTrendSchema = z
    .enum(['new', 'resumed', 'increasing', 'stable', 'decreasing', 'inactive'])
    .describe("Which way this tool's usage is moving week over week, as the Shadow AI screen labels it. Null when there is not enough history to say.")
    .meta({
    id: 'V1ShadowAiTrend',
    title: 'Shadow AI trend',
    description: "Which way a tool's usage is moving week over week. A closed vocabulary: a generated client can switch on it exhaustively.",
});
/**
 * How your developers most often reached a domain.
 *
 * Published as a named enum for the same reason `trend` is — the internal
 * vocabulary is closed (`browserAiNavigationTypeSchema`), and a bare string tells
 * a generated client nothing (@GiorgeCh on #2479).
 *
 * The values are **hyphenated, not the internal slugs**. `NAMING.md` says enum
 * values are "lowercase single words or lowercase-hyphenated, never an internal
 * slug", and `address_bar` / `ads_tracking` are exactly that — the same trap the
 * hook `sourceType` fell into on #2465. So the DTO remaps, as
 * `toPublicHookSourceType` does.
 */
export const v1ShadowAiNavigationTypeSchema = z
    .enum(['real', 'redirect', 'address-bar', 'ads-tracking'])
    .describe('How your endpoint users most often arrived at the domain.')
    .meta({
    id: 'V1ShadowAiNavigationType',
    title: 'Shadow AI navigation type',
    description: 'How your endpoint users most often arrived at a domain. Useful for telling deliberate use from an ad or a redirect.',
});
/**
 * How Kirin classifies an AI tool.
 *
 * **Hyphenated, not the internal snake_case slugs.** The contract previously
 * published `code_assist` / `llm_api` / `ai_browser` raw, as a bare
 * `z.string()` — snake_case in a public body, which `NAMING.md` forbids. The
 * label matters more here than anywhere else on this surface, because
 * `llm_local` reads "Local LLM": the label *inverts* the word order, so a client
 * cannot recover it by de-slugging the value. `categoryName` carries it.
 *
 * A string rather than a frozen enum, deliberately, and for the same reason
 * `V1Platform.id` is: the classification set genuinely grows as new tools are
 * catalogued, and freezing it would make cataloguing a tool a breaking change.
 * The known set is named in the description and on the filter parameter.
 */
export const v1ShadowAiCategorySchema = z
    .string()
    .describe(`How Kirin classifies the tool. Normally one of: ${V1_SHADOW_AI_CATEGORY_IDS.join(', ')}. A newly catalogued classification can appear here, so treat the set as open and read \`categoryName\` for display.`);
/** The classification as the Shadow AI screen labels it. */
export const v1ShadowAiCategoryNameSchema = z
    .string()
    .describe('The classification as the Kirin app labels it — e.g. "Code Assistant" for `code-assist`, "Local LLM" for `llm-local`. Always present; an unrecognised classification is title-cased rather than published as null.');
/**
 * Whether a tool is approved, and by whom and when (KIR-4856, part 3 of
 * KIR-4851) — the Shadow AI screen's "Approval" column.
 *
 * Published as one nested object rather than flat siblings for the same reason
 * `mute`, `exclusion` and `feedback` are on alerts: the verdict, who set it and
 * when travel together.
 *
 * **Null when the `shadow_ai_tool_approval` feature is not enabled for the
 * organization** — the same nullable-object convention those alert fields use
 * for their flags. Reporting `approved: false` instead would be a silent
 * downgrade: an approval stored while the flag was on would read as "not
 * approved". A nullable object is one shape a client must handle; an optional
 * one is two.
 *
 * Approval is per tool for the whole organization, never per row or per
 * endpoint user. Browser tools are approved by domain; installed tools and
 * running processes share one approval per `toolId`, so approving a tool in one
 * approves it in the other.
 */
export const v1ShadowAiApprovalSchema = z
    .object({
    approved: z
        .boolean()
        .describe('Whether an owner or manager approved this tool for your organization. An approved tool stays approved as new activity arrives.'),
    approvedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the tool was approved (ISO 8601 UTC). Null while it is not approved.'),
    approvedBy: v1UserRefSchema
        .nullable()
        .describe('Who approved it. Null while it is not approved, or if that user has since been deleted.'),
})
    .meta({
    id: 'V1ShadowAiApproval',
    title: 'Shadow AI approval',
    description: 'Whether a Shadow AI tool is approved for your organization, and by whom and when. Approval is per tool for the whole organization: browser tools by domain, while installed tools and running processes share one approval per `toolId`.',
    examples: [
        {
            approved: true,
            approvedAt: '2026-09-24T08:12:44Z',
            approvedBy: {
                id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                name: 'Roger Wilco',
                email: 'roger@example.com',
            },
        },
    ],
});
/** The row field, shared by all three lists so one description serves them. */
const approvalField = v1ShadowAiApprovalSchema
    .nullable()
    .describe('Whether this tool is approved, and by whom and when. Null while your organization does not have tool approval (`shadow_ai_tool_approval`) — distinct from `approved: false`, which means the feature is on and nobody has approved it.');
// ============================================================================
// browser-tools
// ============================================================================
/**
 * One AI web app your developers visited.
 *
 * Identified by `domain`, which is a natural key rather than an opaque id — it
 * is the thing itself, so it is stable, meaningful, and safe to construct.
 *
 * **The representative matched URL is not published.** The internal row carries
 * one sample URL behind the detection; a full URL is browsing history and can
 * carry tokens in its query string. The evidence is published as counts and a
 * dominant navigation type instead, which answers "why was this flagged"
 * without shipping the raw trail. Publishing it is a decision to take
 * explicitly rather than by proximity.
 */
export const v1ShadowAiBrowserToolSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this row. Stable, and safe to store, dedupe and join on. Do not parse it: its internal format is not part of this contract and may change. It is **not** the domain — the same domain classified two ways is two rows here, so the identifier covers the whole of what makes a row distinct.'),
    domain: z.string().describe('The domain — for example `chat.openai.com`.'),
    name: z.string().describe('The tool as Kirin names it.'),
    category: v1ShadowAiCategorySchema,
    categoryName: v1ShadowAiCategoryNameSchema,
    trend: v1ShadowAiTrendSchema.nullable(),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users visited it.'),
    endpointCount: z
        .number()
        .int()
        .nullable()
        .describe('How many distinct endpoints (machines) it was seen on. The `endpoint` filter narrows which tools are listed, not this count.'),
    visitCount: z
        .number()
        .int()
        .describe('Total matched visits, including page loads inside frames. `0` on rows recorded before the agent started reporting visit evidence — not a sign of no activity.'),
    realVisitCount: z
        .number()
        .int()
        .describe('Matched visits excluding iframe and subframe loads — the subset most likely to be a person actually using the tool. Also `0` on those older rows.'),
    navigationType: v1ShadowAiNavigationTypeSchema
        .nullable()
        .describe('How your endpoint users most often arrived. Null on rows recorded before this was reported, and on a value Kirin does not recognise.'),
    navigationTypeName: z
        .string()
        .nullable()
        .describe('That arrival route as the Kirin app labels it — e.g. "Ads, tracking & query strings" for `ads-tracking`. Null whenever `navigationType` is. Published because neither the internal slug nor the hyphenated key resembles the label the screen shows.'),
    firstSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When this tool was first detected, ISO 8601 UTC.'),
    lastSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('The most recent detection, ISO 8601 UTC.'),
    approval: approvalField,
})
    .meta({
    id: 'V1ShadowAiBrowserTool',
    title: 'Browser AI tool',
    description: 'One AI web app your endpoint users visited, aggregated across everyone who visited it.',
    examples: [
        {
            id: 'WyJjaGF0Lm9wZW5haS5jb20iLCJsbG0iXQ',
            domain: 'chat.openai.com',
            name: 'ChatGPT',
            category: 'llm',
            categoryName: 'LLM',
            trend: 'increasing',
            userCount: 31,
            endpointCount: 34,
            visitCount: 486,
            realVisitCount: 402,
            navigationType: 'real',
            navigationTypeName: 'Typed or clicked through',
            firstSeenAt: '2026-06-02T08:41:19Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
            approval: {
                approved: true,
                approvedAt: '2026-09-24T08:12:44Z',
                approvedBy: {
                    id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                    name: 'Roger Wilco',
                    email: 'roger@example.com',
                },
            },
        },
    ],
});
/**
 * Sortable fields for browser tools.
 *
 * `loggedInUser` is deliberately absent even though the internal table offers
 * it: that sort is computed in memory over a capped fetch and sets a
 * `truncated` flag, so a caller paging by it would silently miss rows past the
 * cap. A published sort that drops rows is not one to freeze into `v1`.
 */
export const v1ShadowAiBrowserToolSortFieldSchema = z
    .enum([
    'domain',
    'name',
    'category',
    'trend',
    'userCount',
    'firstSeenAt',
    'lastSeenAt',
])
    .describe('Which field to sort by.');
/**
 * The browser-tools list query.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 */
export const v1ShadowAiBrowserToolListQuerySchema = v1PaginationQuerySchema.extend({
    domain: csvParam('Filter by domain.'),
    name: csvParam('Filter by tool name.'),
    category: csvParam('Filter by classification, as returned in `category`. Filter on the key, not the `categoryName` label.'),
    // The ticket names `trend` among this screen's filters and it was missing —
    // sortable but not filterable, with no note saying why (@GiorgeCh on #2479).
    // The accepted values are the closed `V1ShadowAiTrend` vocabulary; the
    // parameter stays a csv string because a `$ref` cannot be expanded into a
    // querystring parameter, so the enum is named in prose.
    trend: csvParam('Filter by trend — `new`, `resumed`, `increasing`, `stable`, `decreasing` or `inactive`.'),
    // KIR-5098: exact, like `site` and running-processes `endpoint`.
    endpoint: csvParam('Keep only tools seen on these endpoints: an exact hostname or machine ID (the `machineId` running-processes publishes). Case-insensitive; surrounding spaces are ignored.'),
    sortBy: v1ShadowAiBrowserToolSortFieldSchema.default('lastSeenAt'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1ShadowAiBrowserToolListResponseSchema = z
    .object({
    data: z
        .array(v1ShadowAiBrowserToolSchema)
        .describe('This page of browser AI tools.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1ShadowAiBrowserToolList',
    title: 'Browser AI tool list',
    description: 'A page of the AI web apps your endpoint users visited. Order is stable across identical calls, so a walk across pages sees each row exactly once.',
    examples: [
        {
            data: [
                {
                    id: 'WyJjaGF0Lm9wZW5haS5jb20iLCJsbG0iXQ',
                    domain: 'chat.openai.com',
                    name: 'ChatGPT',
                    category: 'llm',
                    categoryName: 'LLM',
                    trend: 'increasing',
                    userCount: 31,
                    endpointCount: 34,
                    visitCount: 486,
                    realVisitCount: 402,
                    navigationType: 'real',
                    navigationTypeName: 'Typed or clicked through',
                    firstSeenAt: '2026-06-02T08:41:19Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                    approval: {
                        approved: true,
                        approvedAt: '2026-09-24T08:12:44Z',
                        approvedBy: {
                            id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                            name: 'Roger Wilco',
                            email: 'roger@example.com',
                        },
                    },
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 96,
                totalPages: 4,
                hasMore: true,
            },
        },
    ],
});
// ============================================================================
// installed-tools
// ============================================================================
/**
 * One AI tool installed on your developers' machines.
 *
 * **Per-user detection evidence is not published.** The internal row can carry
 * which sources found the tool and a raw evidence blob, but only when the
 * request narrows to a single developer — org-wide the row is an aggregate
 * where "which sources found it" has no single answer. A field that exists only
 * under one query shape is not one a frozen contract can promise.
 */
export const v1ShadowAiInstalledToolSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this row. Stable, and safe to store, dedupe and join on. Do not parse it: its internal format is not part of this contract and may change. It is **not** the catalog ID — the same tool classified two ways is two rows here, so the identifier covers the whole of what makes a row distinct. The catalog ID is published as `toolId`.'),
    toolId: z
        .string()
        .describe("The tool's identifier within Kirin's catalog."),
    name: z.string().describe('The tool as Kirin names it.'),
    category: v1ShadowAiCategorySchema,
    categoryName: v1ShadowAiCategoryNameSchema,
    risk: v1ShadowAiRiskSchema,
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have it installed.'),
    endpointCount: z
        .number()
        .int()
        .nullable()
        .describe('How many distinct endpoints (machines) it is installed on. The `endpoint` filter narrows which tools are listed, not this count.'),
    firstSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When this tool was first detected, ISO 8601 UTC.'),
    lastSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('The most recent detection, ISO 8601 UTC.'),
    approval: approvalField,
})
    .meta({
    id: 'V1ShadowAiInstalledTool',
    title: 'Installed AI tool',
    description: "One AI tool installed on your endpoint users' machines, aggregated across everyone who has it.",
    examples: [
        {
            id: 'WyJvbGxhbWEiLCJsbG0tbG9jYWwiXQ',
            toolId: 'ollama',
            name: 'Ollama',
            category: 'llm-local',
            categoryName: 'Local LLM',
            risk: 'high',
            userCount: 7,
            endpointCount: 8,
            firstSeenAt: '2026-07-19T10:26:31Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
            approval: { approved: false, approvedAt: null, approvedBy: null },
        },
    ],
});
/** Sortable fields for installed tools. `loggedInUser` omitted — see browser tools. */
export const v1ShadowAiInstalledToolSortFieldSchema = z
    .enum(['name', 'category', 'risk', 'userCount', 'firstSeenAt', 'lastSeenAt'])
    .describe('Which field to sort by.');
/** Deliberately unnamed — see the browser-tools query. */
export const v1ShadowAiInstalledToolListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by tool name.'),
    category: csvParam('Filter by classification, as returned in `category`. Filter on the key, not the `categoryName` label.'),
    risk: csvParam('Filter by risk — `low`, `medium` or `high`.'),
    // KIR-5098: exact, like `site` and running-processes `endpoint`.
    endpoint: csvParam('Keep only tools installed on these endpoints: an exact hostname or machine ID (the `machineId` running-processes publishes). Case-insensitive; surrounding spaces are ignored.'),
    sortBy: v1ShadowAiInstalledToolSortFieldSchema.default('lastSeenAt'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1ShadowAiInstalledToolListResponseSchema = z
    .object({
    data: z
        .array(v1ShadowAiInstalledToolSchema)
        .describe('This page of installed AI tools.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1ShadowAiInstalledToolList',
    title: 'Installed AI tool list',
    description: "A page of the AI tools installed on your endpoint users' machines. Order is stable across identical calls, so a walk across pages sees each row exactly once.",
    examples: [
        {
            data: [
                {
                    id: 'WyJvbGxhbWEiLCJsbG0tbG9jYWwiXQ',
                    toolId: 'ollama',
                    name: 'Ollama',
                    category: 'llm-local',
                    categoryName: 'Local LLM',
                    risk: 'high',
                    userCount: 7,
                    endpointCount: 8,
                    firstSeenAt: '2026-07-19T10:26:31Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                    approval: { approved: false, approvedAt: null, approvedBy: null },
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 24,
                totalPages: 1,
                hasMore: false,
            },
        },
    ],
});
// ============================================================================
// running-processes
// ============================================================================
/** The developer a running process belongs to. */
export const v1ShadowAiProcessUserSchema = z
    .object({
    id: z
        .string()
        .describe('The endpoint user, as identified in the inventory-users resource. Join on this to read their full inventory record.'),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
})
    .meta({
    id: 'V1ShadowAiProcessUser',
    title: 'Process owner',
    description: 'The endpoint user on whose machine a process is running.',
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
        },
    ],
});
/**
 * One ancestor of a running AI process (KIR-4651).
 *
 * Only `pid` is guaranteed. Every other field is a separate read on the
 * endpoint that can fail on its own — a root-owned ancestor is routinely
 * unreadable by an unprivileged agent, and Windows exposes no owning account at
 * all — so a node with nulls is a normal result, not a malformed one.
 */
export const v1ShadowAiProcessAncestorSchema = z
    .object({
    pid: z.number().int().describe('Operating-system process ID.'),
    ppid: z
        .number()
        .int()
        .nullable()
        .describe("This ancestor's own parent ID, or null when it could not be read."),
    name: z
        .string()
        .nullable()
        .describe("The executable's accounting name, e.g. `zsh`, or null when it could not be read."),
    binaryPath: z
        .string()
        .nullable()
        .describe('Path to the executable, or null when it could not be read.'),
    user: z
        .string()
        .nullable()
        .describe('The operating-system account that owns this ancestor, or null when it could not be read. This is the OS account, not the account signed in to any AI tool. Always null on Windows, which exposes no owning account to the agent.'),
    commandLine: z
        .string()
        .nullable()
        .describe("The full command line, or null when it could not be read. Carries the same caveat as the running process's own `commandLine`: **may contain credentials** passed as command-line arguments, and redaction is applied at the source rather than by this API."),
    startedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When this ancestor started, ISO 8601 UTC, or null when it could not be read.'),
})
    .meta({
    id: 'V1ShadowAiProcessAncestor',
    title: 'Process ancestor',
    description: "One process in a running AI process's parent chain.",
});
/**
 * One AI process running right now.
 *
 * A live snapshot, not a history: a process that has exited is not here, so two
 * calls a minute apart legitimately return different rows.
 *
 * **`commandLine` is published in full, unmodified.** Command lines can
 * incidentally contain credentials passed as CLI arguments. Redacting them is
 * **KIR-3649**, which fixes it at the backend layer so the fix reaches every
 * client — this API and the app alike. This surface therefore passes the value
 * straight through rather than redacting locally: a second, `v1`-only
 * implementation would drift from the real one and would hide whether the real
 * one is working. Until KIR-3649 ships, treat this field as potentially
 * sensitive.
 */
export const v1ShadowAiRunningProcessSchema = z
    .object({
    id: z
        .string()
        .describe("This process observation's identifier. Stable for as long as the process is reported."),
    toolId: z
        .string()
        .describe("The tool's identifier within Kirin's catalog — the same value the installed-tools resource publishes as its **`toolId`**, so join on that. It does **not** match that resource's `id`, which is an opaque composite covering the tool *and* its category."),
    name: z.string().describe('The tool as Kirin names it.'),
    category: v1ShadowAiCategorySchema,
    categoryName: v1ShadowAiCategoryNameSchema,
    user: v1ShadowAiProcessUserSchema,
    pid: z.number().int().describe('Operating-system process ID.'),
    port: z
        .number()
        .int()
        .nullable()
        .describe('The port it listens on, or null when it listens on none.'),
    root: z
        .boolean()
        .describe('Whether the process runs as root or administrator. A root-owned AI process can read anything on the machine.'),
    binaryPath: z
        .string()
        .nullable()
        .describe('Path to the executable, or null when it could not be read.'),
    commandLine: z
        .string()
        .nullable()
        .describe('The full command line, or null when it could not be read. **May contain credentials** passed as command-line arguments; redaction is tracked as KIR-3649 and is applied at the source rather than by this API.'),
    parentName: z
        .string()
        .nullable()
        .describe('The parent process, or null when it could not be read. Tells a shell-launched tool from one spawned by an IDE.'),
    machineId: z
        .string()
        .describe('The machine it runs on, as the agent identifies it.'),
    endpointName: z
        .string()
        .nullable()
        .describe("The endpoint's hostname, as the agent last reported it. Null when the endpoint has not reported a hostname, or while endpoint identification is off for the organization. Pair it with `machineId`, which is always present."),
    startedAt: z
        .string()
        .datetime()
        .describe('When the process started, ISO 8601 UTC.'),
    ancestry: z
        .array(v1ShadowAiProcessAncestorSchema)
        .nullable()
        .describe("The process's parent chain, **ordered with the process itself first, then each successive parent**. Answers what launched this process — a bare `node` row means little, its chain back through a shell and a terminal means a great deal.\n\nThe chain **may stop short of PID 1**: the walk ends at the first ancestor the agent may not inspect, which on an unprivileged macOS endpoint is routinely PID 1 itself. A short chain is a normal result; it is never padded with a synthesised entry. Capped at 16 entries.\n\nNull when the endpoint runs an agent predating this feature, or when the walk read nothing."),
    approval: approvalField,
})
    .meta({
    id: 'V1ShadowAiRunningProcess',
    title: 'Running AI process',
    description: "One AI process running on an endpoint user's machine right now. A live snapshot — a process that has exited is not returned.",
    examples: [
        {
            id: 'e0b74c39-5a81-42df-96c7-1f4a8d2b60e5',
            toolId: 'ollama',
            name: 'ollama',
            category: 'llm-local',
            categoryName: 'Local LLM',
            user: {
                id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                name: 'Stella Normal',
                email: 'stella@example.com',
            },
            pid: 48213,
            port: 11434,
            root: false,
            binaryPath: '/usr/local/bin/ollama',
            commandLine: 'ollama serve',
            parentName: 'launchd',
            machineId: 'e85c251bb2c046fa459a6af3b7c1cee2',
            endpointName: 'stella-mbp.local',
            startedAt: '2026-08-31T08:04:52Z',
            ancestry: [
                {
                    pid: 48213,
                    ppid: 1,
                    name: 'ollama',
                    binaryPath: '/usr/local/bin/ollama',
                    user: 'stella',
                    commandLine: 'ollama serve',
                    startedAt: '2026-08-31T08:04:52Z',
                },
                {
                    pid: 1,
                    ppid: 0,
                    name: 'launchd',
                    binaryPath: '/sbin/launchd',
                    user: 'root',
                    commandLine: '/sbin/launchd',
                    startedAt: '2026-08-30T19:11:03Z',
                },
            ],
            approval: { approved: false, approvedAt: null, approvedBy: null },
        },
    ],
});
/** Sortable fields for running processes. `loggedInUser` omitted — see browser tools. */
export const v1ShadowAiRunningProcessSortFieldSchema = z
    .enum(['name', 'category', 'user', 'pid', 'port', 'startedAt'])
    .describe('Which field to sort by.');
/** Deliberately unnamed — see the browser-tools query. */
export const v1ShadowAiRunningProcessListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by tool name.'),
    category: csvParam('Filter by classification, as returned in `category`. Filter on the key, not the `categoryName` label.'),
    // The ticket names `user` among this screen's filters and it was missing —
    // sortable but not filterable, which lets a caller order by developer but
    // not narrow to one (@GiorgeCh on #2479). Takes the same ids `user.id`
    // publishes on every row, and that the inventory-users resource keys on.
    user: csvParam('Filter by endpoint user, using the `user.id` each row publishes.'),
    // KIR-5098: exact, like `site` on browser-agent-sites — "this machine",
    // not every hostname sharing a substring.
    endpoint: csvParam('Filter by endpoint: an exact hostname (`endpointName`) or machine ID (`machineId`). Case-insensitive; surrounding spaces are ignored.'),
    sortBy: v1ShadowAiRunningProcessSortFieldSchema.default('startedAt'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1ShadowAiRunningProcessListResponseSchema = z
    .object({
    data: z
        .array(v1ShadowAiRunningProcessSchema)
        .describe('This page of running AI processes.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1ShadowAiRunningProcessList',
    title: 'Running AI process list',
    description: "A page of the AI processes running on endpoint users' machines right now.",
    examples: [
        {
            data: [
                {
                    id: 'e0b74c39-5a81-42df-96c7-1f4a8d2b60e5',
                    toolId: 'ollama',
                    name: 'ollama',
                    category: 'llm-local',
                    categoryName: 'Local LLM',
                    user: {
                        id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                        name: 'Stella Normal',
                        email: 'stella@example.com',
                    },
                    pid: 48213,
                    port: 11434,
                    root: false,
                    binaryPath: '/usr/local/bin/ollama',
                    commandLine: 'ollama serve',
                    parentName: 'launchd',
                    machineId: 'e85c251bb2c046fa459a6af3b7c1cee2',
                    endpointName: 'stella-mbp.local',
                    startedAt: '2026-08-31T08:04:52Z',
                    ancestry: [
                        {
                            pid: 48213,
                            ppid: 1,
                            name: 'ollama',
                            binaryPath: '/usr/local/bin/ollama',
                            user: 'stella',
                            commandLine: 'ollama serve',
                            startedAt: '2026-08-31T08:04:52Z',
                        },
                        {
                            pid: 1,
                            ppid: 0,
                            name: 'launchd',
                            binaryPath: '/sbin/launchd',
                            user: 'root',
                            commandLine: '/sbin/launchd',
                            startedAt: '2026-08-30T19:11:03Z',
                        },
                    ],
                    approval: { approved: false, approvedAt: null, approvedBy: null },
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 9,
                totalPages: 1,
                hasMore: false,
            },
        },
    ],
});
// ============================================================================
// summary
// ============================================================================
/**
 * Org-wide Shadow AI counters.
 *
 * A single object, not a list — no pagination query and no pagination envelope.
 * It combines the two internal stats endpoints, because "the Shadow AI numbers"
 * is one question and making a caller issue two requests to answer it would
 * publish an internal split as a public shape.
 *
 * **Gated to manager, owner or support**, matching the stricter of the two
 * internal endpoints. Unlike the three lists, these are whole-organization
 * aggregates with nothing to narrow to a single developer, so the answer is to
 * withhold rather than scope.
 *
 * **The top TLS-fingerprint breakdown is not published** — the internal network
 * stats include a per-JA4 list, which is infrastructure detail rather than a
 * Shadow AI finding. The distinct count is published instead.
 */
export const v1ShadowAiSummarySchema = z
    .object({
    tools: z
        .object({
        total: z
            .number()
            .int()
            .describe('Distinct AI tools detected across your organization.'),
        highRisk: z
            .number()
            .int()
            .describe('How many of those Kirin considers high risk.'),
        newThisWeek: z
            .number()
            .int()
            .describe('How many were first detected in the last 7 days.'),
        pendingReview: z
            .number()
            .int()
            .describe('Detections awaiting a classification decision.'),
    })
        .describe('Tool counters.'),
    processes: z
        .object({
        running: z.number().int().describe('AI processes running right now.'),
        rootOwned: z
            .number()
            .int()
            .describe('How many of those run as root or administrator. Worth watching: such a process can read anything on the machine.'),
    })
        .describe('Live process counters.'),
    network: z
        .object({
        aiConnections: z
            .number()
            .int()
            .describe('Observed network connections to AI services.'),
        totalDomains: z
            .number()
            .int()
            .describe('Distinct domains seen in AI-related traffic.'),
        pendingDomains: z
            .number()
            .int()
            .describe('Domains awaiting classification.'),
        approvedDomains: z.number().int().describe('Domains you approved.'),
        flaggedDomains: z.number().int().describe('Domains you flagged.'),
        bytesIn: z
            .number()
            .int()
            .describe('Bytes received over those connections.'),
        bytesOut: z
            .number()
            .int()
            .describe('Bytes sent over those connections. The number to watch for data leaving your network.'),
        uniqueMachines: z
            .number()
            .int()
            .describe('Distinct machines that made those connections.'),
        uniqueTlsFingerprints: z
            .number()
            .int()
            .describe('Distinct TLS (JA4) fingerprints observed — roughly, distinct client software talking to AI services.'),
    })
        .describe('Network counters.'),
})
    .meta({
    id: 'V1ShadowAiSummary',
    title: 'Shadow AI summary',
    description: 'Org-wide Shadow AI counters: what was found, what is running, and what is leaving the network. A single object — this endpoint is not paginated.',
    examples: [
        {
            tools: { total: 24, highRisk: 5, newThisWeek: 3, pendingReview: 8 },
            processes: { running: 9, rootOwned: 1 },
            network: {
                aiConnections: 1842,
                totalDomains: 96,
                pendingDomains: 41,
                approvedDomains: 47,
                flaggedDomains: 8,
                bytesIn: 918273645,
                bytesOut: 132456789,
                uniqueMachines: 63,
                uniqueTlsFingerprints: 12,
            },
        },
    ],
});
// ============================================================================
// browser-agent-sites (KIR-5144)
// ============================================================================
/**
 * Which kind of AI agent reached a site — the key behind the Browser Agent
 * Sites screen's Agent and Via columns.
 *
 * Hyphenated, not the internal snake_case slugs (`chrome_via_cli`), per
 * `NAMING.md`. The screen shows no label for the key itself — it shows the
 * agent (`agentName`) and the medium (`via`), and the row publishes both — so
 * there is no `agentTypeName` sibling to derive.
 */
export const v1ShadowAiBrowserAgentTypeSchema = z
    .enum(['chrome-via-cli', 'chrome-via-browser', 'claude-code-cli'])
    .describe('Which kind of AI agent reached the site.')
    .meta({
    id: 'V1ShadowAiBrowserAgentType',
    title: 'Browser agent type',
    description: 'Which kind of AI agent reached a site. `chrome-via-cli`: Claude Code driving a browser through the Claude in Chrome extension. `chrome-via-browser`: an AI agent running inside the browser itself, such as Claude for Chrome. `claude-code-cli`: Claude Code fetching the site directly, with no browser.',
});
/**
 * The decision an owner or manager set on a site — the screen's Enforcement
 * column. Full vocabulary from day one: `monitor` exists only for
 * `chrome-via-browser` and `block` only for the other two, but a caller
 * switching on this sees every value either way.
 */
export const v1ShadowAiBrowserAgentEnforcementSchema = z
    .enum(['approve', 'block', 'monitor'])
    .describe('The decision set on this site for this agent type.')
    .meta({
    id: 'V1ShadowAiBrowserAgentEnforcement',
    title: 'Browser agent site enforcement',
    description: 'The decision an owner or manager set on a site for one agent type. `approve`: the agent may use it. `block`: Kirin stops the agent reaching it — only `chrome-via-cli` and `claude-code-cli` can be blocked. `monitor`: activity on it raises an alert — the decision available for `chrome-via-browser`, which Kirin can observe but not block.',
});
/**
 * One site an AI agent visited, aggregated across every endpoint user, for one
 * agent type — a row of the Browser Agent Sites screen.
 *
 * **What is withheld.** The row's collector `sources` and the agent's browser
 * extension id are internal evidence the screen does not show as data, and the
 * screen's per-agent-type tab counts are answerable with `agentType=` plus
 * `pagination.totalItems`. None is published.
 */
export const v1ShadowAiBrowserAgentSiteSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this row. Stable, and safe to store, dedupe and join on. Do not parse it: its internal format is not part of this contract and may change. It is **not** the site — the same site reached by two agent types is two rows here.'),
    site: z
        .string()
        .describe('The site the agent reached, as a lowercase host — for example `github.com`.'),
    agentType: v1ShadowAiBrowserAgentTypeSchema,
    agentName: z
        .string()
        .describe('The agent as the Kirin app names it — the "Agent" column, e.g. "Claude Code" or "Claude for Chrome". Display copy; filter on `agentType`.'),
    via: z
        .string()
        .describe('How the agent reached the site, as the Kirin app labels it — the "Via" column, e.g. "Chrome extension", "Chrome / Edge extension" or "Direct fetch". Display copy; filter on `agentType`.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users had this agent reach the site in the last 30 days.'),
    visitCount: z
        .number()
        .int()
        .describe('Visits in the last 30 days, summed across endpoint users — the "Visits (30d)" column. See `visitWindowComplete`.'),
    visitWindowComplete: z
        .boolean()
        .describe('Whether Kirin has a full 30 days of data for this row. `false` means `visitCount` and `userCount` cover a shorter span — the app shows these as "so far".'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent visit, ISO 8601 UTC.'),
    enforcement: v1ShadowAiBrowserAgentEnforcementSchema
        .nullable()
        .describe('The decision an owner or manager set on this site for this agent type. Null while nobody has reviewed it.'),
    reviewedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When `enforcement` was last set (ISO 8601 UTC). Null while nobody has reviewed the site.'),
    reviewedBy: v1UserRefSchema
        .nullable()
        .describe('Who last set `enforcement`. Null while nobody has reviewed the site, or if that user has since been deleted.'),
})
    .meta({
    id: 'V1ShadowAiBrowserAgentSite',
    title: 'Browser agent site',
    description: 'One site an AI agent reached, for one agent type, aggregated across every endpoint user. Lists sites seen in the last 90 days, plus any site that has a decision.',
    examples: [
        {
            id: 'WyJnaXRodWIuY29tIiwiY2hyb21lX3ZpYV9jbGkiXQ',
            site: 'github.com',
            agentType: 'chrome-via-cli',
            agentName: 'Claude Code',
            via: 'Chrome extension',
            userCount: 4,
            visitCount: 57,
            visitWindowComplete: true,
            lastSeenAt: '2026-10-09T13:22:05Z',
            enforcement: 'block',
            reviewedAt: '2026-10-02T09:15:00Z',
            reviewedBy: {
                id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                name: 'Roger Wilco',
                email: 'roger@example.com',
            },
        },
    ],
});
/** Sortable fields for browser agent sites — every sortable column on the screen. */
export const v1ShadowAiBrowserAgentSiteSortFieldSchema = z
    .enum(['site', 'agentName', 'via', 'userCount', 'visitCount', 'lastSeenAt'])
    .describe('Which field to sort by.');
/**
 * The browser-agent-sites list query.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 *
 * Filters are on keys, not on the Agent / Via labels the screen filters on —
 * those are display copy. The date bounds are exclusive, as on alerts.
 */
export const v1ShadowAiBrowserAgentSiteListQuerySchema = v1PaginationQuerySchema.extend({
    site: csvParam('Keep only these sites, matched exactly and case-insensitively against `site`.'),
    agentType: csvParam('Keep only these agent types — `chrome-via-cli`, `chrome-via-browser` or `claude-code-cli`.'),
    enforcement: csvParam('Keep only sites with this decision — `approve`, `block` or `monitor`, or `none` for sites nobody has reviewed.'),
    lastSeenAfter: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only sites last seen strictly after this instant (ISO 8601).'),
    lastSeenBefore: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only sites last seen strictly before this instant (ISO 8601).'),
    sortBy: v1ShadowAiBrowserAgentSiteSortFieldSchema.default('lastSeenAt'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1ShadowAiBrowserAgentSiteListResponseSchema = z
    .object({
    data: z
        .array(v1ShadowAiBrowserAgentSiteSchema)
        .describe('This page of browser agent sites.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1ShadowAiBrowserAgentSiteList',
    title: 'Browser agent site list',
    description: 'A page of the sites AI agents reached on your endpoint users’ machines. Order is stable across identical calls, so a walk across pages sees each row exactly once.',
    examples: [
        {
            data: [
                {
                    id: 'WyJnaXRodWIuY29tIiwiY2hyb21lX3ZpYV9jbGkiXQ',
                    site: 'github.com',
                    agentType: 'chrome-via-cli',
                    agentName: 'Claude Code',
                    via: 'Chrome extension',
                    userCount: 4,
                    visitCount: 57,
                    visitWindowComplete: true,
                    lastSeenAt: '2026-10-09T13:22:05Z',
                    enforcement: 'block',
                    reviewedAt: '2026-10-02T09:15:00Z',
                    reviewedBy: {
                        id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                        name: 'Roger Wilco',
                        email: 'roger@example.com',
                    },
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 12,
                totalPages: 1,
                hasMore: false,
            },
        },
    ],
});
//# sourceMappingURL=shadow-ai.js.map